/**
 * whiteboardCaptureStore — capture photo persistence.
 *
 * Uploads JPEG tiles to Supabase Storage (whiteboard-captures bucket) and stores
 * public URLs on the whiteboard_boards row. IndexedDB keeps a local cache for
 * offline / legacy data URLs from before cloud storage existed.
 */

import { getSupabaseClient, isSupabaseConfigured } from './supabaseClient';
import { getCurrentWorkspaceId, isSupabaseAvailable } from './supabaseSync';
import { getBoard, patchBoardCaptureUrls } from './whiteboardBaselineStore';

const BUCKET = 'whiteboard-captures';
const DB_NAME = 'priorities_whiteboard_captures_v1';
const STORE = 'captures';
const DB_VERSION = 1;

export interface WhiteboardCaptureRecord {
  boardId: string;
  /** Public HTTPS URLs or legacy data URLs. */
  images: string[];
  versionNumber: number;
  updatedAt: string;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error('Could not open capture store'));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'boardId' });
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

/** Downsample a capture to JPEG for upload (keeps projection quality). */
export async function compressCaptureForStorage(dataUrl: string, maxLongEdge = 2400): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const longEdge = Math.max(img.naturalWidth, img.naturalHeight);
      const scale = longEdge <= maxLongEdge ? 1 : maxLongEdge / longEdge;
      const w = Math.max(1, Math.round(img.naturalWidth * scale));
      const h = Math.max(1, Math.round(img.naturalHeight * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Could not process capture'));
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('Could not encode capture'))),
        'image/jpeg',
        0.88,
      );
    };
    img.onerror = () => reject(new Error('Could not read capture image'));
    img.src = dataUrl;
  });
}

function captureObjectPath(
  workspaceId: string,
  boardId: string,
  versionNumber: number,
  tileIndex: number,
): string {
  return `${workspaceId}/${boardId}/v${versionNumber}/tile-${tileIndex}.jpg`;
}

async function writeLocalCache(record: WhiteboardCaptureRecord): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('Capture cache write failed'));
      tx.objectStore(STORE).put(record);
    });
    db.close();
  } catch (err) {
    console.warn('[whiteboardCaptureStore] local cache write failed:', err);
  }
}

async function readLocalCache(boardId: string): Promise<WhiteboardCaptureRecord | null> {
  try {
    const db = await openDb();
    const record = await new Promise<WhiteboardCaptureRecord | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      tx.onerror = () => reject(tx.error ?? new Error('Capture cache read failed'));
      const req = tx.objectStore(STORE).get(boardId);
      req.onsuccess = () => resolve(req.result as WhiteboardCaptureRecord | undefined);
      req.onerror = () => reject(req.error ?? new Error('Capture cache read failed'));
    });
    db.close();
    return record ?? null;
  } catch {
    return null;
  }
}

async function removeLocalCache(boardId: string): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('Capture cache delete failed'));
      tx.objectStore(STORE).delete(boardId);
    });
    db.close();
  } catch (err) {
    console.warn('[whiteboardCaptureStore] local cache delete failed:', err);
  }
}

async function removeCloudCaptureObjects(boardId: string, workspaceId: string | null): Promise<void> {
  if (!workspaceId || !isSupabaseConfigured()) return;
  try {
    const client = getSupabaseClient();
    const prefix = `${workspaceId}/${boardId}`;
    const { data: versionDirs, error: listErr } = await client.storage.from(BUCKET).list(prefix);
    if (listErr || !versionDirs?.length) return;

    const paths: string[] = [];
    for (const dir of versionDirs) {
      const { data: files } = await client.storage.from(BUCKET).list(`${prefix}/${dir.name}`);
      for (const file of files ?? []) {
        paths.push(`${prefix}/${dir.name}/${file.name}`);
      }
    }
    if (paths.length > 0) {
      await client.storage.from(BUCKET).remove(paths);
    }
  } catch (err) {
    console.warn('[whiteboardCaptureStore] cloud delete failed:', err);
  }
}

async function uploadCaptureTiles(
  boardId: string,
  imageDataUrls: string[],
  versionNumber: number,
): Promise<string[] | null> {
  if (!isSupabaseAvailable() || !isSupabaseConfigured()) return null;

  const workspaceId = getCurrentWorkspaceId();
  if (!workspaceId) {
    console.warn('[whiteboardCaptureStore] no workspace id — skipping cloud upload');
    return null;
  }

  try {
    const client = getSupabaseClient();
    const blobs = await Promise.all(imageDataUrls.map((url) => compressCaptureForStorage(url)));
    const publicUrls: string[] = [];

    for (let i = 0; i < blobs.length; i++) {
      const path = captureObjectPath(workspaceId, boardId, versionNumber, i);
      const { error } = await client.storage.from(BUCKET).upload(path, blobs[i], {
        upsert: true,
        contentType: 'image/jpeg',
        cacheControl: '3600',
      });
      if (error) {
        console.warn('[whiteboardCaptureStore] upload failed:', error.message);
        return null;
      }
      const { data } = client.storage.from(BUCKET).getPublicUrl(path);
      publicUrls.push(data.publicUrl);
    }

    return publicUrls;
  } catch (err) {
    console.warn('[whiteboardCaptureStore] upload failed:', err);
    return null;
  }
}

export async function saveBoardCapture(
  boardId: string,
  imageDataUrls: string[],
  versionNumber: number,
): Promise<void> {
  if (imageDataUrls.length === 0) return;

  const updatedAt = new Date().toISOString();
  const cloudUrls = await uploadCaptureTiles(boardId, imageDataUrls, versionNumber);

  if (cloudUrls?.length) {
    await patchBoardCaptureUrls(boardId, cloudUrls);
    await writeLocalCache({ boardId, images: cloudUrls, versionNumber, updatedAt });
    return;
  }

  try {
    const blobs = await Promise.all(imageDataUrls.map((url) => compressCaptureForStorage(url)));
    const images = await Promise.all(
      blobs.map(
        (blob) =>
          new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result));
            reader.onerror = () => reject(reader.error ?? new Error('Could not read blob'));
            reader.readAsDataURL(blob);
          }),
      ),
    );
    await writeLocalCache({ boardId, images, versionNumber, updatedAt });
  } catch (err) {
    console.warn('[whiteboardCaptureStore] save failed:', err);
  }
}

export async function getBoardCapture(boardId: string): Promise<WhiteboardCaptureRecord | null> {
  const board = await getBoard(boardId);
  if (board?.captureImageUrls?.length) {
    return {
      boardId,
      images: board.captureImageUrls,
      versionNumber: board.versionNumber,
      updatedAt: board.updatedAt,
    };
  }
  return readLocalCache(boardId);
}

export async function deleteBoardCapture(boardId: string): Promise<void> {
  await removeCloudCaptureObjects(boardId, getCurrentWorkspaceId());
  await removeLocalCache(boardId);
  const board = await getBoard(boardId);
  if (board?.captureImageUrls?.length) {
    await patchBoardCaptureUrls(boardId, []);
  }
}
